import { Button, Heading, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  EmployerContactFormSchema,
  type EmployerContactFormValues,
  type EmployerContactWritePayload,
} from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import { testIds } from '../../testIds';
import { EmployerContactFields } from '../../components/EmployerContactFields';
import type { EmployerContact } from '../types';

export type ContactPayload = EmployerContactWritePayload;

export function ContactEditForm({
  initialContact,
  onSave,
  onCancel,
  isSaving,
}: {
  initialContact?: EmployerContact;
  onSave: (payload: ContactPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const methods = useForm<EmployerContactFormValues>({
    resolver: zodResolver(EmployerContactFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: initialContact?.firstName ?? '',
      lastName: initialContact?.lastName ?? '',
      email: initialContact?.email ?? '',
      phone: initialContact?.phone ?? '',
      preferredContactMethod: initialContact?.preferredContactMethod ?? '',
      isPrimary: initialContact?.isPrimary ?? false,
    },
  });

  const { handleSubmit } = methods;

  const isEditingPrimary = initialContact?.isPrimary === true;

  const onSubmit = (values: EmployerContactFormValues) => {
    onSave({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim() === '' ? null : values.email.trim(),
      phone: values.phone.trim() === '' ? null : values.phone.trim(),
      preferredContactMethod:
        values.preferredContactMethod === '' ? null : values.preferredContactMethod,
      isPrimary: values.isPrimary,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', height: '100%' }} noValidate>
        <FormMainContainer>
          <FormContentContainer>
            <VStack align="stretch" spacing={4} w="full">
              <Heading fontSize="lg" size="xs" w="full">
                {initialContact != null ? <Trans>Edit contact</Trans> : <Trans>Add contact</Trans>}
              </Heading>

              <EmployerContactFields
                requireName
                showPrimaryToggle
                primaryToggleDisabled={isEditingPrimary}
              />
            </VStack>
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSaving}
              data-testid={testIds.saveEmployerButton}
            >
              <Trans>Save</Trans>
            </Button>
            <Button variant="secondary" onClick={onCancel} isDisabled={isSaving}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      </form>
    </FormProvider>
  );
}
